import { getStateColor } from "./utilities";
import { TrusteeCompanySubscription } from "./shared-interfaces";

export const PAYOUT_VALIDATION_THRESHOLD = 75;

// Display-only label for trustee VAN columns/descriptions. Mirrors what the
// backend used to emit via `trustee.vans[].label` before that helper was
// removed in favor of returning the raw subscriptions.
export const TRUSTEE_VAN_LABEL_BY_FUND_CODE: Record<string, string> = {
  FD00000210: "PAPS VAN",
  FD00000031: "POPS VAN",
};

export function trusteeVanLabelFor(
  subscription: TrusteeCompanySubscription,
): string {
  const fundCode = subscription.product.fundCode || "";
  return (
    TRUSTEE_VAN_LABEL_BY_FUND_CODE[fundCode] ||
    `${subscription.product.name} VAN`
  );
}
export const ghanaCardRegex = /^[A-Z]{3}-[0-9]{9}-[0-9A-Za-z]{1}$/;
export const TEMPLATE_API_ENDPOINT = `${process.env.REACT_APP_API_URL}/dataset`;
export const PAYMENT_STATEMENT_TEMPLATE_API_ENDPOINT = `${process.env.REACT_APP_API_URL}/template_dataset?key=bulk_upload_payment_statement`;
export const phoneRegex = /^\d{12}$/;
export const invalidPhoneMessage = "Please enter a valid number";
export const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const invalidEmailMessage = "Please enter a valid email address";
export const invalidGhanaCardMessage =
  "Invalid format. Expected: (GHA-000000000-0)  or (country code-000000000-0) for foreigners";
export const LOAN_REQUEST_STATES = [
  {
    label: "Pending KYC",
    value: "pending_kyc",
    color: getStateColor("pending_kyc"),
  },
  {
    label: "Awaiting Application Forms",
    value: "awaiting_application_forms",
    color: getStateColor("awaiting_application_forms"),
  },
  {
    label: "Under Review",
    value: "under_review",
    color: getStateColor("under_review"),
  },
  {
    label: "Pending Ops Processing",
    value: "pending_ops_processing",
    color: getStateColor("pending_ops_processing"),
  },
  {
    label: "Pending Ops Approval",
    value: "pending_ops_approval",
    color: getStateColor("pending_ops_approval"),
  },
  {
    label: "Sent to Provider",
    value: "sent_to_provider",
    color: getStateColor("sent_to_provider"),
  },
  { label: "Hold", value: "hold", color: getStateColor("hold") },
  { label: "Approved", value: "approved", color: getStateColor("approved") },
  { label: "Disbursed", value: "disbursed", color: getStateColor("disbursed") },
  { label: "Accepted", value: "accepted", color: getStateColor("accepted") },
  { label: "Rejected", value: "rejected", color: getStateColor("rejected") },
  { label: "Cancelled", value: "cancelled", color: getStateColor("cancelled") },
];

export const loanTenureMonths = [
  { id: "3", name: "3 Months", value: "3" },
  { id: "4", name: "4 Months", value: "4" },
  { id: "5", name: "5 Months", value: "5" },
  { id: "6", name: "6 Months", value: "6" },
  { id: "7", name: "7 Months", value: "7" },
  { id: "8", name: "8 Months", value: "8" },
  { id: "9", name: "9 Months", value: "9" },
  { id: "10", name: "10 Months", value: "10" },
  { id: "11", name: "11 Months", value: "11" },
  { id: "12", name: "12 Months (1 Year)", value: "12" },
  { id: "13", name: "13 Months", value: "13" },
  { id: "14", name: "14 Months", value: "14" },
  { id: "15", name: "15 Months", value: "15" },
  { id: "16", name: "16 Months", value: "16" },
  { id: "17", name: "17 Months", value: "17" },
  { id: "18", name: "18 Months", value: "18" },
  { id: "19", name: "19 Months", value: "19" },
  { id: "20", name: "20 Months", value: "20" },
  { id: "21", name: "21 Months", value: "21" },
  { id: "22", name: "22 Months", value: "22" },
  { id: "23", name: "23 Months", value: "23" },
  { id: "24", name: "24 Months (2 Years)", value: "24" },
  { id: "25", name: "25 Months", value: "25" },
  { id: "26", name: "26 Months", value: "26" },
  { id: "27", name: "27 Months", value: "27" },
  { id: "28", name: "28 Months", value: "28" },
  { id: "29", name: "29 Months", value: "29" },
  { id: "30", name: "30 Months", value: "30" },
  { id: "31", name: "31 Months", value: "31" },
  { id: "32", name: "32 Months", value: "32" },
  { id: "33", name: "33 Months", value: "33" },
  { id: "34", name: "34 Months", value: "34" },
  { id: "35", name: "35 Months", value: "35" },
  { id: "36", name: "36 Months (3 Years)", value: "36" },
  { id: "48", name: "48 Months (4 Years)", value: "48" },
  { id: "60", name: "60 Months (5 Years)", value: "60" },
  { id: "72", name: "72 Months (6 Years)", value: "72" },
  { id: "84", name: "84 Months (7 Years)", value: "84" },
];
export const contributionTypes = [
  {
    id: "employee_post_tax",
    name: "Employee Post Tax",
    value: "employee_post_tax",
  },
  {
    id: "employee_pre_tax",
    name: "Employee Pre Tax",
    value: "employee_pre_tax",
  },
  {
    id: "employer_post_tax",
    name: "Employer Post Tax",
    value: "employer_post_tax",
  },
  {
    id: "employer_pre_tax",
    name: "Employer Pre Tax",
    value: "employer_pre_tax",
  },
];
export const loanSecurityTypes = [
  { id: "secured", name: "Secured", value: "secured" },
  { id: "semi_secured", name: "Semi Secured", value: "semi_secured" },
  { id: "unsecured", name: "Unsecured", value: "unsecured" },
];
export const loanTypes = [
  { id: "new_loan", name: "New Loan", value: "new_loan" },
  // { id: "take_over", name: "Take Over", value: "take_over" },
  // { id: "top_up", name: "Top Up", value: "top_up" },
];

export const paymentMethods = [
  {
    id: "bank_transfer",
    name: "Bank Transfer",
    value: "bank_transfer",
  },
  {
    id: "cheque",
    name: "Cheque",
    value: "cheque",
  },
  {
    id: "cash",
    name: "Cash",
    value: "cash",
  },
  {
    id: "mobile_money",
    name: "Mobile Money",
    value: "mobile_money",
  },
];

export const interactionCategoryOptions = [
  { id: "general_request", name: "General Request" },
  { id: "complaint", name: "Complaint" },
];

export const investmentActivities = [
  {
    id: "short_term",
    name: "Short Term (< 1 year)",
    value: "short_term",
  },
  {
    id: "medium_term",
    name: "Medium Term (1-5 years)",
    value: "medium_term",
  },
  {
    id: "long_term",
    name: "Long Term (> 5 years)",
    value: "long_term",
  },
  {
    id: "retirement",
    name: "Retirement Planning",
    value: "retirement",
  },
  {
    id: "wealth_preservation",
    name: "Wealth Preservation",
    value: "wealth_preservation",
  },
];

export const tradeFrequencies = [
  {
    id: "daily",
    name: "Daily",
    value: "daily",
  },
  {
    id: "weekly",
    name: "Weekly",
    value: "weekly",
  },
  {
    id: "monthly",
    name: "Monthly",
    value: "monthly",
  },
  {
    id: "quarterly",
    name: "Quarterly",
    value: "quarterly",
  },
  {
    id: "annually",
    name: "Annually",
    value: "annually",
  },
  {
    id: "infrequent",
    name: "Infrequent",
    value: "infrequent",
  },
];

export const FIXED_DEPOSIT_REQUEST_STATES = [
  {
    label: "Pending KYC",
    value: "pending_kyc",
    color: "orange",
  },
  {
    label: "Under Review",
    value: "under_review",
    color: "blue",
  },
  {
    label: "Pending Ops Processing",
    value: "pending_ops_processing",
    color: "orange",
  },
  {
    label: "Pending Ops Approval",
    value: "pending_ops_approval",
    color: "orange",
  },
  {
    label: "Sent to Provider",
    value: "sent_to_provider",
    color: "purple",
  },
  {
    label: "Approved",
    value: "approved",
    color: "green",
  },
  {
    label: "Rejected",
    value: "rejected",
    color: "red",
  },
  {
    label: "Cancelled",
    value: "cancelled",
    color: "red",
  },
  {
    label: "Hold",
    value: "hold",
    color: "gold",
  },
  {
    label: "Accepted",
    value: "accepted",
    color: "green",
  },
  {
    label: "Active",
    value: "active",
    color: "green",
  },
  {
    label: "Matured",
    value: "matured",
    color: "cyan",
  },
  {
    label: "Closed",
    value: "closed",
    color: "default",
  },
];

export const FIXED_INCOME_REQUEST_STATES = [
  {
    label: "Pending KYC",
    value: "pending_kyc",
    color: "orange",
  },
  {
    label: "Pending Approval",
    value: "pending_approval",
    color: "purple",
  },
  {
    label: "Approved",
    value: "approved",
    color: "green",
  },
  {
    label: "Pending Payment Verification",
    value: "pending_payment_verification",
    color: "pink",
  },
  {
    label: "Pending Compliance Review",
    value: "pending_compliance_review",
    color: "gold",
  },
  {
    label: "Payment Verified",
    value: "payment_verified",
    color: "cyan",
  },
  {
    label: "Cancelled",
    value: "cancelled",
    color: "red",
  },
  {
    label: "Hold",
    value: "hold",
    color: "default",
  },
  {
    label: "Purchased",
    value: "purchased",
    color: "green",
  },
];

export const fixedIncomePaymentMethods = [
  {
    id: "bank_transfer",
    name: "Bank Transfer",
    value: "bank_transfer",
  },
  {
    id: "mobile_money",
    name: "Mobile Money",
    value: "mobile_money",
  },
];

export const fixedIncomeRequestTypes = [
  { id: "fixed_deposit", name: "Fixed Income", value: "fixed_deposit" },
];

export const residentialStatuses = [
  {
    id: "resident_ghanaian",
    name: "Resident Ghanaian",
    value: "resident_ghanaian",
  },
  {
    id: "resident_foreigner",
    name: "Resident Foreigner",
    value: "resident_foreigner",
  },
  {
    id: "non_resident_ghanaian",
    name: "Non Resident Ghanaian",
    value: "non_resident_ghanaian",
  },
  {
    id: "non_resident_foreigner",
    name: "Non Resident Foreigner",
    value: "non_resident_foreigner",
  },
  {
    id: "refugee_and_asylum_seeker",
    name: "Refugee and Asylum Seeker",
    value: "refugee_and_asylum_seeker",
  },
];

export const amountRangesOptions = [
  { id: "below_1000", name: "Below 1,000", value: "below_1000" },
  { id: "1001_5000", name: "1,001 - 5,000", value: "1001_5000" },
  { id: "5001_10000", name: "5,001 - 10,000", value: "5001_10000" },
  { id: "above_10000", name: "Above 10,000", value: "above_10000" },
];

export const frequencyRangesOptions = [
  { id: "monthly", name: "Monthly", value: "monthly" },
  { id: "quarterly", name: "Quarterly", value: "quarterly" },
  { id: "bi_annually", name: "Bi-Annually", value: "bi_annually" },
  { id: "annually", name: "Annually", value: "annually" },
  { id: "others", name: "Others", value: "others" },
];

export const frequencyOfIncomeOptions = [
  { id: "0", name: "Daily", value: "daily" },
  { id: "1", name: "Twice A Month", value: "twice_a_month" },
  { id: "2", name: "Monthly", value: "monthly" },
];

export const natureOfEmploymentOptions = [
  { id: "0", name: "Hazardous", value: "hazardous" },
  { id: "1", name: "Non-Hazardous", value: "non_hazardous" },
];

export const DEBOUNCE_TIME = 500;

export const PAGE_SIZE = 25;

export const tabPaneStyle: React.CSSProperties = {
  backgroundColor: "#52c41a",
  position: "relative",
  left: "7px",
};

export const MANUAL_TAG_NAMES = ["Closed", "Suspended"];

export const AUTOMATIC_TAG_NAMES = [
  "Active",
  "Inactive",
  "Awaiting First Payment",
  "Kindergarten",
  "Dormant",
  "Non-Paying",
  "Ported Out",
];

export const UNEDITABLE_LOAN_STATES = [
  "approved",
  "disbursed",
  "sent_to_provider",
  "rejected",
  "pending_ops_processing",
  "pending_ops_approval",
];
